/**
 * localStorage-backed cache for the outcomes-dashboard preferences.
 *
 * This is the cache the React Query layer reads through {@link readPrefsCache}
 * for instant first paint and writes through {@link writePrefsCache} after a
 * successful server save. It is intentionally outcomes-scoped — not a generic
 * preferences cache — so the storage key and shape are owned here.
 *
 * Every access is wrapped so a disabled, full, or corrupt localStorage never
 * throws into the render path; on any failure the cache behaves as empty.
 */

/** @typedef {import("./outcomes-prefs").OutcomesPrefs} OutcomesPrefs */

export const PREFS_CACHE_KEY = "transfr:dashboard:outcomes:prefs:v1";

/**
 * Reads the cached outcomes prefs, or `undefined` when there is nothing valid
 * cached (missing, unavailable storage, or unparseable JSON).
 *
 * @returns {OutcomesPrefs|undefined}
 */
export function readPrefsCache() {
  try {
    const raw = window.localStorage.getItem(PREFS_CACHE_KEY);
    if (!raw) {
      return;
    }
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === "object" ? parsed : undefined;
  } catch {
    return;
  }
}

/**
 * Persists the outcomes prefs to localStorage. A storage failure (quota,
 * disabled storage) is swallowed — the cache is a first-paint optimization,
 * never the source of truth.
 *
 * @param {OutcomesPrefs} prefs
 */
export function writePrefsCache(prefs) {
  try {
    window.localStorage.setItem(PREFS_CACHE_KEY, JSON.stringify(prefs));
  } catch {
    // Storage unavailable or full: server remains the source of truth.
  }
}

/**
 * Removes the cached outcomes prefs. Used when switching identities (e.g.
 * impersonation) so a prior user's selections can't bleed through.
 */
export function clearPrefsCache() {
  try {
    window.localStorage.removeItem(PREFS_CACHE_KEY);
  } catch {
    // Nothing to do — treat an unavailable store as already clear.
  }
}
